import type {
  RequestType,
  RestContentType,
  RestMethod,
  RestParam,
  StoredHistoryEntry,
  StoredQuery,
} from "./persistence";
import { normalizeRestPathAndParams } from "@spuerhund/shared/queryMetadata";
import type { QueryExecutionResult } from "./resultTypes";

export interface HistoryReplayEditorState {
  queryText: string;
  variables: string;
  headers: string;
  requestType: RequestType;
  restMethod: RestMethod;
  restPath: string;
  restParams: RestParam[];
  restContentType: RestContentType;
}

export interface HistoryReplayResultState {
  result: QueryExecutionResult | null;
  error: string | null;
}

function defaultRequestType(requestType: RequestType | undefined): RequestType {
  return requestType ?? "graphql";
}

function defaultRestMethod(restMethod: string | undefined): RestMethod {
  return (restMethod as RestMethod | undefined) ?? "GET";
}

function defaultRestPath(restPath: string | undefined): string {
  return restPath ?? "/";
}

function defaultRestContentType(restContentType: RestContentType | undefined): RestContentType {
  return restContentType ?? "json";
}

function looksLikeJson(text: string): boolean {
  const trimmed = text.trim();
  return trimmed.startsWith("{") || trimmed.startsWith("[");
}

export function restoreEditorStateFromHistory(
  entry: Pick<
    StoredHistoryEntry,
    "queryText" | "variables" | "headers" | "requestType" | "restMethod" | "restPath" | "restContentType"
  >,
): HistoryReplayEditorState {
  // History only stores the URL path (with any query string), so extract any
  // params back out so the editor's Params grid shows them correctly.
  const { restPath, restParams } = normalizeRestPathAndParams(entry.restPath, undefined);
  return {
    queryText: entry.queryText,
    variables: entry.variables || "{}",
    headers: entry.headers || "{}",
    requestType: defaultRequestType(entry.requestType),
    restMethod: defaultRestMethod(entry.restMethod),
    restPath: defaultRestPath(restPath),
    restParams,
    restContentType: defaultRestContentType(entry.restContentType),
  };
}

export function restoreEditorStateFromQuery(
  query: Pick<
    StoredQuery,
    "queryText" | "variables" | "headers" | "requestType" | "restMethod" | "restPath" | "restParams" | "restContentType"
  >,
): HistoryReplayEditorState {
  return {
    queryText: query.queryText,
    variables: query.variables,
    headers: query.headers,
    requestType: defaultRequestType(query.requestType),
    restMethod: defaultRestMethod(query.restMethod),
    restPath: defaultRestPath(query.restPath),
    restParams: query.restParams ?? [],
    restContentType: defaultRestContentType(query.restContentType),
  };
}

export function restoreResultStateFromHistory(entry: StoredHistoryEntry): HistoryReplayResultState {
  const statusCode = entry.statusCode ?? 200;
  const durationMs = entry.durationMs ?? 0;
  const requestType = defaultRequestType(entry.requestType);
  const rawBody = entry.responseBody ?? entry.responseJson ?? "";

  if (!rawBody) {
    return {
      result: null,
      error: entry.errorMessage ?? null,
    };
  }

  if (requestType === "rest") {
    let parsedData: unknown;
    const contentType = entry.responseContentType ?? (looksLikeJson(rawBody) ? "application/json" : "text/plain");

    if (contentType.includes("json")) {
      try {
        parsedData = JSON.parse(rawBody);
      } catch {
        parsedData = undefined;
      }
    }

    return {
      result: {
        requestType,
        ...(parsedData !== undefined ? { data: parsedData } : {}),
        statusCode,
        durationMs,
        responseBody: rawBody,
        responseContentType: contentType,
      },
      error: null,
    };
  }

  if (requestType === "sql") {
    try {
      const json = JSON.parse(rawBody) as {
        command?: string;
        rowCount?: number;
        rows?: unknown[];
      };
      return {
        result: {
          requestType,
          ...(Array.isArray(json.rows) ? { data: json.rows } : {}),
          statusCode,
          durationMs,
          responseBody: rawBody,
          responseContentType: "application/json",
          ...(json.command ? { command: json.command } : {}),
          ...(typeof json.rowCount === "number" ? { rowCount: json.rowCount } : {}),
        },
        error: null,
      };
    } catch {
      // Fall through to raw rendering below.
    }
  }

  if (entry.responseJson) {
    try {
      const json = JSON.parse(entry.responseJson) as {
        data?: unknown;
        errors?: unknown[];
      };
      return {
        result: {
          requestType,
          ...(json.data !== undefined ? { data: json.data } : {}),
          ...(json.errors ? { errors: json.errors } : {}),
          statusCode,
          durationMs,
        },
        error: null,
      };
    } catch {
      // Fall back to raw body rendering below.
    }
  }

  return {
    result: {
      requestType,
      statusCode,
      durationMs,
      responseBody: rawBody,
      ...(entry.responseContentType ? { responseContentType: entry.responseContentType } : {}),
    },
    error: null,
  };
}
